import {Component, OnInit} from '@angular/core';
import {IDialogView} from '@shared/components/ok-cancel-dialog/ok-cancel-dialog.component';
import {DialogModel} from '@shared/models/dialog.model';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {QuestionnaireFieldConfig} from '@shared/models/common.models';
import {DataMappingSourceOption} from '@features/data-mapping/models/data-mapping.model';
import {PlatformQuery} from '@shared/models/platform-query/platform-query';
import {PlatformQueryService} from '@core/services/platform-query.service';
import {delay, switchMap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {FieldDefinition} from '@shared/models/field-definition';
import {Api} from '@shared/constants/common-field.constants';

@Component({
    standalone: false,
    selector: 'app-create-data-mapping-dialog',
  templateUrl: './create-data-mapping-dialog.component.html',
  styleUrls: ['./create-data-mapping-dialog.component.scss']
})
export class CreateDataMappingDialogComponent implements OnInit, IDialogView {
  data: DialogModel;
  dataMappingForm: FormGroup;
  sourceFiledKeys$: Observable<any[]>;
  sourceFieldKeys: any[] = [];
  destinationFieldKeys$: Observable<any[]>;
  destinationFieldKeys: any[] = [];
  selectedSource: QuestionnaireFieldConfig;
  selectedDestination: QuestionnaireFieldConfig;
  sourceOption: DataMappingSourceOption;
  destinationOption: DataMappingSourceOption;
  selectedDestinationItem: any;
  selectedDestinationItems: any;
  selectedSourceItem: any;
  selectedSourceItems: any;
  source = new FormControl(null, [Validators.required]);
  destination = new FormControl(null, [Validators.required]);
  private splitOwnership: boolean;

  constructor(private fb: FormBuilder, private platformQueryService: PlatformQueryService) {
    this.dataMappingForm = this.fb.group({
      dataMappingName: ['', [Validators.required]],
      source: this.source,
      destination: this.destination
    });
  }

  ngOnInit(): void {
    this.data.isDisabledOkButton = true;
    this.dataMappingForm.valueChanges.subscribe((value) => {
      console.log(value);
      this.data.isDisabledOkButton = this.dataMappingForm.invalid;
    });
  }

  onOkClicked(): boolean {
    const hasSourceOwnership = this.sourceFieldKeys.findIndex(o => o.dataType === 'Ownership');
    const hasDestinationOwnership = this.destinationFieldKeys.findIndex(o => o.dataType === 'Ownership');

    if (this.dataMappingForm.valid) {
      this.data.input.dataMappingName = this.dataMappingForm.get('dataMappingName').value;
      this.data.input.selectedSource = this.selectedSource;
      this.data.input.selectedDestination = this.selectedDestination;
      this.data.input.selectedDestinationItem = this.selectedDestinationItem;
      this.data.input.selectedSourceItem = this.selectedSourceItem;
      // tslint:disable-next-line:no-bitwise
      this.data.input.splitOwnership = (hasSourceOwnership ^ hasDestinationOwnership) < 0;
      return true;
    }
    return false;
  }

  onSourceSelectionChange($event: any) {
    const {sourceOption, selectedSource, selectedItems, selectedItem} = $event;
    this.sourceOption = sourceOption;
    this.selectedSource = selectedSource;
    this.selectedSourceItem = selectedItem;
    this.selectedSourceItems = selectedItems;
    if (this.sourceOption.id === 'itp-fields') {
      this.selectedSource = selectedSource;
      if (this.selectedSourceItem) {
        this.selectedSourceItem = null;
      }
      this.sourceFiledKeys$ = this.getFieldKeys$(this.selectedSource);
    }
    if (this.sourceOption.id === 'questionnaire') {
      this.selectedSource = selectedSource;
      if (this.selectedSource) {
        this.selectedSource = null;
      }
      this.sourceFiledKeys$ = this.getQuestionnaireKeys$(this.selectedSourceItem);
    }
    if (this.sourceFiledKeys$) {
      this.sourceFiledKeys$.subscribe(res => this.sourceFieldKeys = res);
    }
  }

  onDestinationSelectionChange($event: any) {
    const {sourceOption, selectedSource, selectedItems, selectedItem} = $event;
    this.destinationOption = sourceOption;
    this.selectedDestinationItems = selectedItems;
    this.selectedDestinationItem = selectedItem;
    if (this.destinationOption.id === 'itp-fields') {
      this.selectedDestination = selectedSource;
      if (this.selectedDestinationItem) {
        this.selectedDestinationItem = null;
      }
      this.destinationFieldKeys$ = this.getFieldKeys$(this.selectedDestination);
    }
    if (this.destinationOption.id === 'questionnaire') {
      this.selectedDestinationItem = selectedItem;
      if (this.selectedDestination) {
        this.selectedDestination = null;
      }
      this.destinationFieldKeys$ = this.getQuestionnaireKeys$(this.selectedDestinationItem);
    }
    this.destinationFieldKeys$.subscribe(res => this.destinationFieldKeys = res);
  }

  private getFieldKeys$(selectedConfig: QuestionnaireFieldConfig): Observable<FieldDefinition[]> {
    const platformQuery = new PlatformQuery();
    platformQuery.api = selectedConfig.apiName;
    platformQuery.name = 'FieldDefinitionsQuery';
    platformQuery.isCamelCase = true;
    platformQuery.fieldValues['SourceType'] = selectedConfig.sourceType;
    platformQuery.fieldValues['SectionHeaders'] = selectedConfig.sectionHeaders;
    return this.platformQueryService.executeQuery(platformQuery).pipe(switchMap(res => {
      if (res && res.fieldValues) {
        const fieldDefinitions = res.fieldValues['FieldDefinitions'].filter(item => item.header !== 'Actions' && item.isUIField === false);
        return of(fieldDefinitions).pipe(delay(0));
      }
    }));
  }

  private getQuestionnaireKeys$(selectedItem: any) {
    const query = new PlatformQuery();
    query.name = 'QuestionnaireQuery';
    query.api = Api.FileStoreApi;
    query.fieldValues['IncludeFlowchartSection'] = true;
    query.fieldValues['FileRelativePath'] = selectedItem.relativePath;
    return this.platformQueryService.executeQuery(query).pipe(switchMap(res => {
      if (res && res.fieldValues) {
        const flowchartSections = res.fieldValues['FlowchartSection'];
        return of(flowchartSections.questions).pipe(delay(0));
      }
    }));
  }
}
